// guidesSite.js - Shared building blocks for the static /guides/ site
// (slugs, URLs, sitemap.xml, guides-registry.js and the list of city pages).
// Used by GenerateAllSeoButton and GenerateIndexOnlyButton so both produce the same files.
import { CITY_CONTENT } from '../../data/cityPages';

export const SITE_BASE = 'https://guidesofia.com';

// A city gets its own page (/guides/<country>/<city>/) when it has at least this many guide pages
// (each language version counts: Lisbon has 72 pages for 28 tours).
// Cities with a content file in src/data/cityPages always get one.
export const MIN_GUIDES_FOR_CITY_PAGE = 5;

const countryMap = {
  'es': 'Spain', 'pt': 'Portugal', 'fr': 'France', 'it': 'Italy',
  'de': 'Germany', 'gb': 'United Kingdom', 'us': 'United States',
  'br': 'Brazil', 'mx': 'Mexico', 'cn': 'China', 'jp': 'Japan',
  'au': 'Australia', 'nz': 'New Zealand', 'in': 'India', 'za': 'South Africa',
  'bg': 'Bulgaria', 'ua': 'Ukraine', 'uk': 'United Kingdom', 'un': 'United Arab Emirates', 'ae': 'United Arab Emirates',
  'pl': 'Poland', 'nl': 'Netherlands', 'be': 'Belgium', 'ch': 'Switzerland',
  'at': 'Austria', 'cz': 'Czech Republic', 'hu': 'Hungary', 'ro': 'Romania',
  'gr': 'Greece', 'tr': 'Turkey', 'il': 'Israel', 'th': 'Thailand',
  'sg': 'Singapore', 'hk': 'Hong Kong', 'kr': 'South Korea', 'id': 'Indonesia',
  'ar': 'Argentina', 'co': 'Colombia', 'cl': 'Chile', 'pe': 'Peru',
  'ca': 'Canada', 'ie': 'Ireland', 'se': 'Sweden', 'no': 'Norway',
  'dk': 'Denmark', 'fi': 'Finland', 'ru': 'Russia', 'ma': 'Morocco',
  'eg': 'Egypt', 'ke': 'Kenya', 'ng': 'Nigeria', 'gh': 'Ghana'
};

export const getCountryName = (code) => {
  if (!code) return 'World';
  return countryMap[String(code).toLowerCase()] || code;
};

export const createSlug = (str) => {
  if (!str) return 'guide';
  const result = String(str)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')   // strip latin diacritics
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    // keep: latin alphanum, cyrillic, arabic, hebrew, CJK, hyphens
    .replace(/[^\wЀ-ӿ؀-ۿ֐-׿一-鿿-]/g, '')
    .replace(/-+/g, '-')
    .replace(/(^-|-$)/g, '');
  return result || 'guide';
};

export const escapeHtml = (str) => {
  if (str === undefined || str === null) return '';
  return String(str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
};

export const escapeXml = (str) => {
  if (!str) return '';
  return String(str)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;')
    .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
};

const jsString = (str) => String(str || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\r?\n/g, ' ');

// The page address of a guide-list entry: the real one when the page was just generated
// (it comes from the full guide data, which can differ from the list), otherwise derived from the list.
export const entryUrl = (g) => g.pageUrl || guideUrl(g.Name.trim(), g.Country);

export const guideUrl = (name, countryCode) => {
  const countrySlug = createSlug(getCountryName(countryCode));
  const guideSlug = createSlug(name);
  return `/guides/${countrySlug}/${guideSlug}/${guideSlug}-seo.html`;
};

// A first-stop player whose audio could not be packaged still points at remote storage, which may refuse it.
// Remove the player instead of publishing a button that does not play.
export const removeUnpackagedAudio = (html) =>
  html.replace(/\s*<div class="stop-audio">[\s\S]*?<audio[^>]*src="https?:\/\/[^"]*"[^>]*>\s*<\/audio>\s*<\/div>/g, '');

// ─── Cities ──────────────────────────────────────────────────────────────────

// A city page is named after its city (/guides/spain/madrid/madrid.html), like the guide pages are named after
// their guide. The folder's index.html only forwards there, so older links to /guides/<country>/<city>/ still work.
export const cityPageUrl = (countrySlug, citySlug) => `/guides/${countrySlug}/${citySlug}/${citySlug}.html`;

/** The folder's index.html: sends visitors and search engines on to the city page. */
export const forwardHtml = (url, title) => `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>${escapeHtml(title)}</title>
  <link rel="canonical" href="${SITE_BASE}${escapeHtml(url)}">
  <meta name="robots" content="noindex, follow">
  <meta http-equiv="refresh" content="0; url=${escapeHtml(url)}">
  <script>location.replace(${JSON.stringify(url)} + location.hash);</script>
</head>
<body><p><a href="${escapeHtml(url)}">${escapeHtml(title)}</a></p></body>
</html>
`;

// "Lisboa" and "Lisbon" are the same city: content files list their aliases.
const ALIASES = {};
Object.entries(CITY_CONTENT).forEach(([key, content]) => {
  (content.aliases || []).forEach(alias => { ALIASES[createSlug(alias)] = key.split('/')[1]; });
});

// "Rio De Janeiro" → "Rio de Janeiro": the small words inside a place name stay lower case
const PLACE_SMALL_WORDS = new Set(['da', 'das', 'de', 'del', 'des', 'di', 'do', 'dos', 'du', 'e', 'el', 'la', 'las', 'le', 'los', 'y']);
export const placeName = (locality) => {
  const name = String(locality || '').trim().replace(/\s+/g, ' ');
  if (!name || name === 'Unknown') return '';
  return name.split(' ').map((w, i) => (i > 0 && PLACE_SMALL_WORDS.has(w.toLowerCase()) ? w.toLowerCase() : w)).join(' ');
};

export const citySlugOf = (locality) => {
  const slug = createSlug(String(locality || '').trim());
  return ALIASES[slug] || slug;
};

export const cityKeyOf = (guide) => `${createSlug(getCountryName(guide.Country || ''))}/${citySlugOf(guide.Locality)}`;

/**
 * Which cities get a page. Works on the guide list (Name, Locality, Country), so the index-only button
 * and the full generator agree, plus the planner's research (public/city-research/index.json from
 * guide-studio/city_pages.py): a city that passes the research bar gets a page with or without guides.
 * Returns [{ key, citySlug, countrySlug, city, country, url, count, aliases, research, center, photo }].
 */
export const findCityPages = (guides, research = {}) => {
  const researched = Object.entries(research.cities || {})
    .filter(([, r]) => (r.readiness || 0) >= (research.min_readiness || 0));
  const passes = Object.fromEntries(researched);
  const byKey = {};
  guides.filter(g => g.Name && g.Locality && String(g.Locality).trim() && g.Locality !== 'Unknown').forEach(g => {
    const key = cityKeyOf(g);
    if (!byKey[key]) byKey[key] = [];
    byKey[key].push(g);
  });
  return Object.entries(byKey)
    .filter(([key, list]) => CITY_CONTENT[key] || list.length >= MIN_GUIDES_FOR_CITY_PAGE || passes[key])
    .map(([key, list]) => {
      const [countrySlug, citySlug] = key.split('/');
      const content = CITY_CONTENT[key] || {};
      // most common spelling wins unless the content file names the city
      const counts = {};
      list.forEach(g => { const n = String(g.Locality).trim(); counts[n] = (counts[n] || 0) + 1; });
      const commonName = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      return {
        key, countrySlug, citySlug,
        city: content.name || placeName(commonName),
        country: getCountryName(list[0].Country || ''),
        url: cityPageUrl(countrySlug, citySlug),
        count: list.length,
        aliases: Object.keys(counts),
        ...researchFields(key, passes[key]),
      };
    })
    // cities with research and no published guide yet: a planning page, which becomes a guide hub later
    .concat(researched.filter(([key]) => !byKey[key]).map(([key, r]) => {
      const [countrySlug, citySlug] = key.split('/');
      return { key, countrySlug, citySlug, city: (CITY_CONTENT[key] || {}).name || placeName(r.city), country: r.country,
        url: cityPageUrl(countrySlug, citySlug), count: 0, aliases: [], ...researchFields(key, r) };
    }))
    .sort((a, b) => (b.count - a.count) || ((b.research ? 1 : 0) - (a.research ? 1 : 0)));
};

const researchFields = (key, r) => {
  if (!r) return { research: false };
  const [countrySlug, citySlug] = key.split('/');
  return { research: true, center: r.center || null,
    photo: r.photo ? `/guides/${countrySlug}/${citySlug}/img/places/${r.photo}` : '' };
};

/** "3 audio guides", or "Travel guide" for a city page without guides yet. */
export const cityPageLabel = (c) => (c.count ? `${c.count} audio ${c.count === 1 ? 'guide' : 'guides'}` : 'Travel guide');

// ─── guides-registry.js ──────────────────────────────────────────────────────

export const buildRegistry = (guides, cityPages = []) => {
  const entries = guides
    .filter(g => g.Name)
    .map(g => ({
      name: g.Name.trim(),
      city: g.Locality || 'Unknown',
      country: getCountryName(g.Country || ''),
      url: entryUrl(g),
      description: (g.Description || '').slice(0, 80),
      thumbnail: g.Photo || '',
    }));

  return `/**
 * GuideSofia SEO Pages Registry
 * Auto-generated — do not edit manually
 * Generated: ${new Date().toLocaleString()}
 * Total guides: ${entries.length}
 */

window.GuidesRegistry = [
${entries.map(e => `  {
    name: "${jsString(e.name)}",
    city: "${jsString(e.city)}",
    country: "${jsString(e.country)}",
    url: "${jsString(e.url)}",
    description: "${jsString(e.description)}",
    thumbnail: "${jsString(e.thumbnail)}"
  }`).join(',\n')}
];

// City pages (/guides/<country>/<city>/). seo-scripts.js links each guide page to its city page.
window.GuidesCityPages = [
${cityPages.map(c => `  { city: "${jsString(c.city)}", country: "${jsString(c.country)}", url: "${jsString(c.url)}", count: ${c.count}, aliases: [${c.aliases.map(a => `"${jsString(a)}"`).join(', ')}] }`).join(',\n')}
];
`;
};

// ─── sitemap.xml ─────────────────────────────────────────────────────────────
// index.html and the country pages come from HubPageGenerator.js.

export const buildSitemapXml = (guides, cityPages = [], countryPages = []) => {
  const today = new Date().toISOString().split('T')[0];
  const entry = (loc, freq, priority) => `
  <url>
    <loc>${escapeXml(loc)}</loc>
    <lastmod>${today}</lastmod>
    <changefreq>${freq}</changefreq>
    <priority>${priority}</priority>
  </url>`;
  const countries = countryPages.map(k => entry(SITE_BASE + k.url, 'weekly', '0.9')).join('');
  const cities = cityPages.map(c => entry(SITE_BASE + c.url, 'weekly', '0.9')).join('');
  const urls = guides
    .filter(g => g.Name)
    .map(g => entry(SITE_BASE + entryUrl(g), 'monthly', '0.8')).join('');

  return `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${entry(SITE_BASE + '/guides/', 'weekly', '0.9')}${countries}${cities}${urls}

</urlset>`;
};
